/**
 * 테두리 좌표 캐싱 유틸리티
 * 성능 최적화를 위해 동일한 속성의 테두리 좌표를 캐싱합니다.
 */

interface BorderCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface BorderCacheKey {
  x: number;
  y: number;
  width: number;
  height: number;
  borderWidth: number;
  borderPosition: 'outside' | 'center' | 'inside';
}

// 테두리 좌표 캐시
const borderCache = new Map<string, BorderCoordinates>();

/**
 * 캐시 키 생성
 */
const getCacheKey = (key: BorderCacheKey): string => {
  return `${key.x.toFixed(2)}:${key.y.toFixed(2)}:${key.width.toFixed(2)}:${key.height.toFixed(2)}:${key.borderWidth}:${key.borderPosition}`;
};

/**
 * 테두리 좌표를 계산하거나 캐시에서 반환합니다
 * @param x 객체 X 좌표
 * @param y 객체 Y 좌표
 * @param width 객체 너비
 * @param height 객체 높이
 * @param borderWidth 테두리 두께
 * @param borderPosition 테두리 위치
 * @returns 테두리 좌표
 */
export const getBorderCoordinates = (
  x: number,
  y: number,
  width: number,
  height: number,
  borderWidth: number,
  borderPosition: 'outside' | 'center' | 'inside' = 'center'
): BorderCoordinates => {
  const cacheKey: BorderCacheKey = { x, y, width, height, borderWidth, borderPosition };
  const key = getCacheKey(cacheKey);
  
  if (borderCache.has(key)) {
    return borderCache.get(key)!;
  }
  
  // 테두리 좌표 계산
  const halfBorder = borderWidth / 2;
  let coords: BorderCoordinates;
  
  switch (borderPosition) {
    case 'outside':
      coords = {
        x: x - halfBorder,
        y: y - halfBorder,
        width: width + borderWidth,
        height: height + borderWidth
      };
      break;
    case 'inside':
      coords = {
        x: x + borderWidth,
        y: y + borderWidth,
        width: width - borderWidth * 2,
        height: height - borderWidth * 2
      };
      break;
    case 'center':
    default:
      coords = { x, y, width, height };
      break;
  }
  
  borderCache.set(key, coords);
  return coords;
};

/**
 * Stroke를 위한 픽셀 정렬 (선명한 테두리를 위해)
 * 홀수 두께일 때 0.5px 오프셋을 추가하여 픽셀 경계에 맞춤
 * @param borderPosition 테두리 위치 (inside일 때 외부 경계가 객체 경계와 일치하도록 보정)
 */
export const alignForStroke = (
  x: number,
  y: number,
  width: number,
  height: number,
  lineWidth: number,
  borderPosition: 'outside' | 'center' | 'inside' = 'center'
): BorderCoordinates => {
  if (borderPosition === 'inside') {
    // inside 테두리: 테두리의 외부 경계가 객체 경계와 일치하도록 보정
    // strokeRect는 테두리 중심선이 경계에 위치하므로, 중심선을 borderWidth/2만큼 안쪽으로 이동
    const halfBorder = lineWidth / 2;
    const adjustedX = x - halfBorder;
    const adjustedY = y - halfBorder;
    const adjustedWidth = width + lineWidth;
    const adjustedHeight = height + lineWidth;
    
    // 픽셀 정렬: 홀수 두께일 때는 오프셋을 빼는 방향으로 조정
    const offset = (Math.round(lineWidth) % 2 !== 0) ? -0.5 : 0;
    return {
      x: Math.round(adjustedX) + offset,
      y: Math.round(adjustedY) + offset,
      width: Math.round(adjustedWidth),
      height: Math.round(adjustedHeight)
    };
  }
  
  // center/outside: 기존 로직 유지
  const offset = (Math.round(lineWidth) % 2 !== 0) ? 0.5 : 0;
  return {
    x: Math.round(x) + offset,
    y: Math.round(y) + offset,
    width: Math.round(width),
    height: Math.round(height)
  };
};

/**
 * 캐시를 초기화합니다 (테스트 또는 메모리 관리용)
 */
export const clearBorderCache = (): void => {
  borderCache.clear();
};

